import { AuditOutcome } from 'models/internalAudit';
import { htmlToText } from 'utils/html';

export const auditConclusionMaxLength = 32 * 1024;

// Where a procedure/auditee proposal on the execute tab came from
// (InternalAuditProcedureRules.ProposalSources on the API)
export enum AuditProposalSource {
  None = 0,
  Bound = 1,
  History = 2,
  Form = 3,
  AI = 4,
  Manual = 5,
  // 6 (Series) and 7 (Library) were the per-series binding rows, retired 2026-09-11: never reuse
}

// Server-computed read models: no clone/isEqual because the execute tab never dirty-checks them.

export class InternalAuditExecutionItem {
  entityType: number;

  entityId: number;

  code?: string;

  name?: string;

  outcome: AuditOutcome;

  conclusion?: string;

  // controls and requirements only
  assessedState?: number;

  writeBackState?: number;

  assessedById?: string;

  assessedOn?: Date;

  // the audit day the item is planned on; undefined = the default day (the audit's start date)
  plannedOn?: Date;

  taskId?: number;

  // the execution task's own name
  taskName?: string;

  // the procedure the task was built from; absent without a task or when the library lost it
  procedureTaskId?: number;

  procedureName?: string;

  taskStateId?: number;

  taskCompleted: boolean;

  assigneeId?: string;

  proposedTemplateTaskId?: number;

  proposedTemplateTaskName?: string;

  proposedUserId?: string;

  proposalSource: AuditProposalSource;

  // a non-conformity with no finding following up on it: publishing is refused while this is set
  needsFinding: boolean;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.outcome = AuditOutcome.NotAssessed;
    this.taskCompleted = false;
    this.proposalSource = AuditProposalSource.None;
    this.needsFinding = false;
  }
}

export class InternalAuditProcedure {
  taskId: number;

  name?: string;

  constructor() {
    this.taskId = 0;
  }
}

export class InternalAuditExecution {
  internalAuditId: number;

  items: InternalAuditExecutionItem[];

  procedures: InternalAuditProcedure[];

  constructor() {
    this.internalAuditId = 0;
    this.items = [];
    this.procedures = [];
  }
}

// One scope item's assessment with the audit it was made in; doubles as the previous-result answer
export class InternalAuditScopeAssessment {
  internalAuditId: number;

  auditName?: string;

  auditStartDate?: Date;

  auditEndDate?: Date;

  entityType: number;

  entityId: number;

  outcome: AuditOutcome;

  conclusion?: string;

  assessedState?: number;

  writeBackState?: number;

  assessedById?: string;

  assessedOn?: Date;

  constructor() {
    this.internalAuditId = 0;
    this.entityType = 0;
    this.entityId = 0;
    this.outcome = AuditOutcome.NotAssessed;
  }
}

export class InternalAuditTaskExecutionItem {
  entityType: number;

  entityId: number;

  code?: string;

  name?: string;

  outcome: AuditOutcome;

  conclusion?: string;

  assessedById?: string;

  assessedOn?: Date;

  previous?: InternalAuditScopeAssessment;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.outcome = AuditOutcome.NotAssessed;
  }
}

// The auditee task UI read model: the audit, the scope item(s) the task covers and their results
export class InternalAuditTaskExecution {
  internalAuditId: number;

  auditName?: string;

  auditStatus: number;

  // the caller may edit the task from the audit: its assignee or owner, or a lead auditor or auditor
  canEdit: boolean;

  items: InternalAuditTaskExecutionItem[];

  constructor() {
    this.internalAuditId = 0;
    this.auditStatus = 0;
    this.canEdit = false;
    this.items = [];
  }
}

// A finished execution task is the audit's evidence: the outcome lands on the audit's scope row and
// the conclusion states what was examined. Finishing with neither stored would close the audit with
// nothing recorded, so completion is blocked until both are filled in.
export const auditConclusionMinLength = 3;

export enum AuditResultBlock {
  None = 0,
  NoOutcome = 1,
  ShortConclusion = 2,
}

// Judged on the editor: the result is stored by the task save, so what is typed is what is saved
export const getAuditResultBlock = (outcome: AuditOutcome, conclusion: string | undefined): AuditResultBlock => {
  if (outcome === AuditOutcome.NotAssessed) return AuditResultBlock.NoOutcome;
  if (htmlToText(conclusion).trim().length < auditConclusionMinLength) return AuditResultBlock.ShortConclusion;

  return AuditResultBlock.None;
};

export class InternalAuditProcedureSuggestion {
  entityType: number;

  entityId: number;

  templateTaskId: number;

  reason?: string;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.templateTaskId = 0;
  }
}
